import React, { useState, useEffect, useRef } from "react"; // 1. useRef를 import 합니다.
import { useLocation } from "react-router-dom";
import SearchList from "../../components/List/searchList";
import Loading from "../../components/Common-components/loading";
import { apiGet } from "../../utils/api";
import { usePageTitle } from '../../contexts/PageTitleContext'
import '../../css/Search/search.css'
import { SearchBtn, Dot} from "../../assets/icons";

export default function Search() {
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const [optionLabels, setOptionLabels] = useState({}); // 옵션 라벨 상태 추가
  const location = useLocation();
  const { setPageTitle } = usePageTitle();

  // 2. SearchGroup 컴포넌트를 참조하기 위한 ref를 생성합니다.
  const searchGroupRef = useRef(null);

  const paramToLabelMap = {
    r: "운항 지역",
    d: "출발 항구",
    v: "기항지",
    m: "출발월",
    n: "여행 기간",
    l: "선사명",
  };

  // URL 파라미터를 객체로 변환
  const getSearchParams = () => {
    const params = new URLSearchParams(location.search);
    const result = {};
    params.forEach((value, key) => {
      if (value !== "") {
        result[key] = value;
      }
    });
    return result;
  };

  // 검색 옵션 정보를 불러오는 함수
  const fetchSearchOptions = async () => {
    try {
      const data = await apiGet("/search/get_search_option.php");
      const labels = {};

      const categories = [
        { apiField: 'destinations', paramKey: 'r' },
        { apiField: 'departure_ports', paramKey: 'd' },
        { apiField: 'ports', paramKey: 'v' },
        { apiField: 'months', paramKey: 'm' },
        { apiField: 'spreadnights', paramKey: 'n' },
        { apiField: 'cruise_lines', paramKey: 'l' },
        { apiField: 'ship', paramKey: 's' },
      ];

      categories.forEach(category => {
        labels[category.paramKey] = {};
        if (data[category.apiField]) {
          data[category.apiField].forEach(item => {
            labels[category.paramKey][item.id] = item.label;
          });
        }
      });
      setOptionLabels(labels);
    } catch (error) {
      console.error("검색 옵션 정보를 불러오지 못했습니다:", error);
    }
  };

  // 가격이 모두 비어 있는 일정 제거
  const hasVisiblePrice = (schedule) => {
    if (!schedule || typeof schedule !== 'object') return false;
    const leadprice = schedule.leadprice;
    const leadNumeric = typeof leadprice === 'number'
      ? leadprice
      : Number(String(leadprice ?? '').replace(/,/g, ''));
    if (Number.isFinite(leadNumeric) && leadNumeric > 0) return true;
    const priceFields = ['cruiseinside', 'cruiseoutside', 'cruisebalcony', 'cruisesuite'];
    return priceFields.some((field) => {
      const raw = schedule[field];
      if (raw == null || raw === '') return false;
      const numeric = typeof raw === 'number' ? raw : Number(String(raw).replace(/,/g, ''));
      return Number.isFinite(numeric) && numeric > 0;
    });
  };

  const sanitizeSearchResults = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const filteredValue = Array.isArray(item.value)
          ? item.value.filter(hasVisiblePrice)
          : [];
        if (!filteredValue.length) return null;
        return { ...item, value: filteredValue };
      })
      .filter(Boolean);
  };

  // 페이지 로드시 API 요청
  useEffect(() => {
    setPageTitle('검색결과');
    const currentSearchParams = getSearchParams();
    setSearchParams(currentSearchParams);
    fetchSearchOptions(); // 검색 옵션 정보 불러오기
    if (Object.keys(currentSearchParams).length > 0) {
      fetchSearchResults(currentSearchParams);
    }
  }, [location.search, setPageTitle]);

  // API 요청 함수
  const fetchSearchResults = async (params) => {
    setIsLoading(true);
    setShowLoading(true);
    try {
      const data = await apiGet("/search/get_search_list.php", params);
      setSearchResults(sanitizeSearchResults(data));
    } catch (error) {
      console.error("검색 실패:", error);
    } finally {
      setIsLoading(false);
      setTimeout(() => setShowLoading(false), 500);
    }
  };

  // 3. param-value div를 클릭했을 때 실행될 핸들러 함수입니다.
  const handleParamValueClick = () => {
    // searchGroupRef.current가 유효하면(DOM에 연결되어 있으면) click 이벤트를 실행합니다.
    if (searchGroupRef.current) {
      // isMobile 상태일 때 모달을 여는 버튼을 클릭하도록 합니다.
      const mobileButton = searchGroupRef.current.querySelector('.mobile-search-button');
      if (mobileButton) {
        mobileButton.click();
      }
    }
  };

  // 선택한 값들의 라벨만 그대로 나열 (모든 키 공통)
  const formatParamLabel = (key, rawValue) => {
    const parts = String(rawValue)
      .split(',')
      .map((v) => String(v).trim())
      .filter((v) => v !== '');

    const numericKey = (s, idx) => {
      // YYYY-MM -> yyyymm 숫자로 변환
      if (/^\d{4}-\d{2}$/.test(s)) {
        const [y, m] = s.split('-');
        return parseInt(y, 10) * 100 + parseInt(m, 10);
      }
      const n = Number(s);
      return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER + idx; // 숫자가 아니면 뒤쪽으로
    };

    const sorted = parts
      .map((s, idx) => ({ s, n: numericKey(s, idx), idx }))
      .sort((a, b) => a.n - b.n || a.idx - b.idx)
      .map(({ s }) => optionLabels[key]?.[s] ?? s);

    // console.log(sorted);
    return sorted.join(', ');
  };

  return (
    <div className="search-wrapper">{/* 기본 padding 0 40px 적용 */}
      <h2 className="search-page-title">검색결과</h2>
      {/* 4. div에 onClick 이벤트를 연결 */}
      <div className="param-value" onClick={handleParamValueClick}>
        <SearchBtn className="search-icon" style={{width:'12px', height:'12px'}}/>
        <div className="param-value-wrapper">
        {Object.entries(searchParams).map(([key, value], index) => (
          <div key={key}>
            {index > 0 && <Dot className="dot-icon" />}
            <span className="search-value-item">
              {/* {paramToLabelMap[key]}:  */}
              {/* {optionLabels[key] && optionLabels[key][value] ? optionLabels[key][value] : value} */}
              {formatParamLabel(key, value)}
            </span>
          </div>
        ))}
        </div>
      </div>
      {/* 5. 생성한 ref를 searchGroupRef라는 prop으로 SearchList에 전달합니다. */}
      <SearchList
        searchResults={searchResults}
        currentSearchParams={searchParams}
        optionLabels={optionLabels}
        searchGroupRef={searchGroupRef}
      />
      {showLoading && <Loading isLoading={isLoading} />}
    </div>
  );
}
